/**
 * The alert groups' contract, and what came with it — bata-platform PR #496 (owner rulings DEC-101,
 * DEC-102, DEC-103), as the pinned client generates it. `docs/adr/9040`.
 *
 * Shared, not a feature's: the groups and their acts are Management's (`src/features/foundations/`),
 * the assignee picker is used by the in-season decision bar too (`src/planning/assignee-picker.tsx`),
 * and the group counts are the shell's badge (`src/app/routes/my-work/`). One file, so the re-pin was
 * one swap.
 *
 * ══ PINNED ═══════════════════════════════════════════════════════════════════
 *
 * `src/api/spec/openapi.json` is bata-platform v0.58.0, which publishes the four routes (#496,
 * merged; its spec is #496's branch spec at 9898488 / fd9baf5 but for the version string). So the
 * types below are the generated ones, re-exported under the names the screens, the picker and the
 * tests already import (aliased where the generated name differs), and the four operation constants
 * are the generated operations. Until this re-pin the file held #496's shapes typed by hand and the
 * constants were `null`; the generated shapes matched them field for field, except that the
 * generator types every query member optional and every optional member nullable — the page's
 * query and filter keep their required members and drop `null` (`WithoutNulls`), as
 * `ActivityQuery` does (ADR 9037), and stay assignable to the generated shapes.
 *
 * The page's vocabularies stay here, because each word has words in the three catalogues; a
 * compile-time check holds each equal to the generated enum, so a word the platform adds or drops
 * fails the type check until the catalogues follow.
 *
 * `alert-groups.test.tsx` keeps the tripwire: the pinned spec has each path exactly when its
 * constant is wired. A platform that does not serve the routes yet answers 404, which the pages
 * draw as «In arrivo».
 */

import {
  listAlertAssignees, listAlertGroups, postMySession, recordAlertGroupAction,
  type AlertAssigneeItem, type AlertAssignees, type AlertCounts, type AlertEventSummary, type AlertGroup,
  type AlertGroupAct, type AlertGroupActionFilterBody, type AlertGroupActionRequest, type AlertGroupActionResult,
  type AlertGroupAssignee, type AlertGroupDimension, type AlertGroupPage, type AlertGroupSkip, type AlertGroupSkipReason,
  type AlertGroupTotals, type AlertSummary, type ListAlertAssigneesParams, type ListAlertGroupsParams, type LocalisedText,
  type RecordAlertGroupActionParams,
} from '../generated';

export type {
  AlertAssigneeItem, AlertAssignees, AlertGroup, AlertGroupAct, AlertGroupActionRequest, AlertGroupActionResult,
  AlertGroupAssignee, AlertGroupDimension, AlertGroupPage, AlertGroupSkip, AlertGroupSkipReason, AlertGroupTotals,
  ListAlertAssigneesParams, ListAlertGroupsParams,
};

/** True exactly when `A` and `B` are the same type — the compile-time half of the vocabulary checks. */
type Equals<A, B> = (<T>() => T extends A ? 1 : 2) extends (<T>() => T extends B ? 1 : 2) ? true : false;

/**
 * A generated request shape as the pages send it: every member without `null`. The generator types
 * an optional member `T | null`; the pages never send `null` (the no-value bucket is `-`), and
 * `exactOptionalPropertyTypes` would otherwise make every reader handle a value it never builds. The
 * result is assignable to the generated shape, so it goes on the wire unchanged.
 */
export type WithoutNulls<T> = { readonly [K in keyof T]: Exclude<T[K], null> };

/** The filter value that selects the alerts with NO value for a key (`store_key=-`, ADR 2109). */
export const NO_VALUE = '-';

/** The platform's localised text: a stable key and its words for the caller's language. */
export type PendingLocalisedText = LocalisedText;

// ── the groups read ──────────────────────────────────────────────────────────

/** The dimensions a group is cut by — DEC-101: market (default), store, department. */
export const ALERT_GROUP_DIMENSIONS = ['country', 'store', 'department'] as const satisfies readonly AlertGroupDimension[];
const DIMENSIONS_MATCH: Equals<(typeof ALERT_GROUP_DIMENSIONS)[number], AlertGroupDimension> = true;

/**
 * The read's parameters as the pages send them: the generated query, with `group_by` required (the
 * generator types every query member optional). `group_by=department` requires `country_key`
 * (422/409, as ADR 2106).
 */
export type AlertGroupQuery = WithoutNulls<NonNullable<ListAlertGroupsParams['query']>> & { readonly group_by: AlertGroupDimension };

export type AlertGroupsRead = typeof listAlertGroups;

// ── the group acts ───────────────────────────────────────────────────────────

/** The four acts a group admits (DEC-90: on a filter, never on picked rows). */
export const ALERT_GROUP_ACTS = ['assign', 'unassign', 'snooze', 'dispose'] as const satisfies readonly AlertGroupAct[];
const ACTS_MATCH: Equals<(typeof ALERT_GROUP_ACTS)[number], AlertGroupAct> = true;

/** The set an act takes — reconstructible from these fields alone (`selected_filter`, reqs/07 §3.5 d). */
export type AlertGroupFilter = WithoutNulls<AlertGroupActionFilterBody>;

/** Why an alert of the set was not moved — the platform's vocabulary. */
export const ALERT_GROUP_SKIP_REASONS = [
  'already_decided', 'snooze_not_future', 'snooze_beyond_limit', 'concurrent',
] as const satisfies readonly AlertGroupSkipReason[];
const SKIP_REASONS_MATCH: Equals<(typeof ALERT_GROUP_SKIP_REASONS)[number], AlertGroupSkipReason> = true;

export type AlertGroupActionParams = RecordAlertGroupActionParams;

export type AlertGroupActionOp = typeof recordAlertGroupAction;

/** 409: the filter matches other than `expectedCount` now; `currentCount` on the document. */
export const ALERT_GROUP_CHANGED = 'alert_group_changed';
/** 422: the set is above the bound; `currentCount` and `maximumCount` on the document. */
export const ALERT_GROUP_TOO_LARGE = 'alert_group_too_large';

// ── the assignee picker (DEC-102) ────────────────────────────────────────────

export type AlertAssigneesRead = typeof listAlertAssignees;

// ── the session's claims, copied once (DEC-102) ──────────────────────────────

/**
 * `POST /api/v1/me/session` — the idempotent write that copies the signed-in person's claims (name,
 * roles) to their profile, so they appear in the assignee list. No body; an `Idempotency-Key` like
 * every POST; 200 with the `UserProfile` (`GET /me/profile`'s shape). Called once per sign-in
 * (`src/auth/session-claims-sync.ts`).
 */
export type SessionClaimsSyncOp = typeof postMySession;

// ── additive members of pinned shapes ────────────────────────────────────────

/** `/me/work-counts`: the generated `AlertCounts` now carries `assignedGroups` and `claimableGroups`. */
export type AlertCountsWithGroups = AlertCounts;

/** The generated `AlertSummary` now carries the assignee's name, from the profile. */
export type AlertSummaryWithName = AlertSummary;

/** The generated `AlertEventSummary` now carries the `groupActionId` of the group act that recorded it. */
export type AlertEventWithGroup = AlertEventSummary;

// ── the operations ───────────────────────────────────────────────────────────

/**
 * The generated operations. Typed `| null` still, so a test (and a component's `read`/`act` prop)
 * can mount the "not pinned" state by passing `null` explicitly.
 */
export const ALERT_GROUPS_READ: AlertGroupsRead | null = listAlertGroups;
export const ALERT_GROUP_ACTION: AlertGroupActionOp | null = recordAlertGroupAction;
export const ALERT_ASSIGNEES_READ: AlertAssigneesRead | null = listAlertAssignees;
export const SESSION_CLAIMS_SYNC: SessionClaimsSyncOp | null = postMySession;

/** The vocabulary checks, held so the compiler reports a mismatch where it is declared. */
export const ALERT_GROUP_VOCABULARIES_MATCH = DIMENSIONS_MATCH && ACTS_MATCH && SKIP_REASONS_MATCH;

/** The paths the re-pin tripwire looks for in the spec. */
export const ALERT_GROUPS_PATH_IN_SPEC = '/api/v1/foundations/exceptions/groups';
export const ALERT_GROUP_ACTION_PATH_IN_SPEC = '/api/v1/foundations/exceptions/group-actions';
export const ALERT_ASSIGNEES_PATH_IN_SPEC = '/api/v1/foundations/exceptions/assignees';
export const SESSION_CLAIMS_PATH_IN_SPEC = '/api/v1/me/session';
